import {
  Button,
  Center,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spacer,
  useDisclosure,
} from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import { MultiMonthCalendar } from "@src/components/ui-kit";
import { Icon } from "@src/components/ui-kit/Icon";
import { InputDate } from "@src/components/ui-kit/InputDatePicker/input-date";
import { dateTime } from "@src/utils/formatters";
import { observer, useLocalObservable } from "mobx-react-lite";
import React, { type FunctionComponent, useContext } from "react";
import { DateType, StoreDateLimitationCtx } from "./StoreDateLimitation";

export const DateLimitationPicker: FunctionComponent<
  React.PropsWithChildren<{ onSubmit: () => void }>
> = observer(function DateLimitationPicker({ onSubmit }) {
  const PLACEHOLDERS = {
    default: t`Choose date range`,
    from: t`From`,
    to: t`Until`,
  };

  const store = useContext(StoreDateLimitationCtx);
  const dp = useDisclosure();

  const state = useLocalObservable(() => ({
    get activePlaceholder() {
      if (!store.enabled) {
        return PLACEHOLDERS.default;
      }
      if (store.type === DateType.Range) {
        return `${dateTime.format(store.selectedDates.start)} - ${dateTime.format(
          store.selectedDates.end,
        )}`;
      }
      if (store.type === DateType.Until) {
        return `${PLACEHOLDERS.to} - ${dateTime.format(store.selectedDates.end)}`;
      }
      if (store.type === DateType.From) {
        return `${PLACEHOLDERS.from} - ${dateTime.format(store.selectedDates.start)}`;
      }
      return PLACEHOLDERS.default;
    },
  }));

  return (
    <React.Fragment>
      <Button
        w="100%"
        fontWeight="normal"
        isDisabled={!store.enabled}
        {...dp.getButtonProps()}
        rightIcon={<Icon name="chevron-down" />}
        variant="outline"
      >
        {state.activePlaceholder}
      </Button>
      <DateModal {...dp.getDisclosureProps()} {...dp} onSubmit={onSubmit} />
    </React.Fragment>
  );
});

const DateModal: FunctionComponent<
  React.PropsWithChildren<{
    onSubmit: () => void;
    isOpen: boolean;
    onClose: () => void;
  }>
> = observer(function DateModal({ onSubmit, isOpen, onClose }) {
  const store = useContext(StoreDateLimitationCtx);

  const state = useLocalObservable(() => ({
    selectedStartMonth: store.selectedDates?.start
      ? store.selectedDates.start.getMonth()
      : store.month,
    selectedStartYear: store.selectedDates?.start
      ? store.selectedDates.start.getFullYear()
      : store.year,
    selectedEndMonth: store.selectedDates?.end
      ? store.selectedDates.end.getMonth()
      : store.month,
    selectedEndYear: store.selectedDates?.end
      ? store.selectedDates.end.getFullYear()
      : store.year,
    get selected() {
      return {
        start: store.selectedDates.start,
        end: store.selectedDates.end,
      };
    },
  }));

  return (
    <Modal
      isCentered
      isOpen={isOpen}
      onClose={onClose}
      size="2xl"
      trapFocus={false}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Trans>Choose date limitation</Trans>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <HStack justify="space-around" spacing="2">
            <InputDate
              label={t`From`}
              value={store.inputFromValue}
              w="full"
              onChange={store.handleChangeInputFromValue}
            />
            <InputDate
              label={t`Until`}
              value={store.inputToValue}
              w="full"
              onChange={store.handleChangeInputToValue}
            />
          </HStack>

          <Center mt="4">
            <MultiMonthCalendar
              hideCalendarActions
              value={state.selected}
              onSelectDate={store.handleSelectDate}
            />
          </Center>
        </ModalBody>
        <ModalFooter gap="2">
          <Button onClick={store.handleClear} variant="ghost">
            <Trans>Clear</Trans>
          </Button>
          <Spacer />
          <Button onClick={onClose} variant="outline">
            <Trans>Cancel</Trans>
          </Button>
          <Button
            onClick={() => {
              onClose();
              onSubmit();
            }}
          >
            <Trans>Save</Trans>
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
});
